import {
  normalizeCanvasViewportTarget,
  type CanvasViewportTarget,
} from "./canvasViewportTarget";

/**
 * The URL form of a viewport target: `/canvas/:canvasId#x=100&y=100&z=0.5&t=2000`
 * (and `/view/canvas/:canvasId#…` on the public viewer). It lives in the hash
 * because a hash change is a same-document navigation: following one never
 * reloads the app, from the address bar or from a link alike. The hash is
 * read as `URLSearchParams`; the keys are the short ones agents write by
 * hand, and this is the only place that knows them.
 */
export const CANVAS_VIEWPORT_HASH_KEYS = {
  x: "x",
  y: "y",
  zoom: "z",
  duration: "t",
} as const satisfies Record<keyof CanvasViewportTarget, string>;

function hashParams(hash: string): URLSearchParams {
  return new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
}

/** `hash` as `location.hash` gives it, with or without the leading `#`.
 *  Null when it holds no usable target (see normalizeCanvasViewportTarget). */
export function parseCanvasViewportHash(
  hash: string,
): CanvasViewportTarget | null {
  const params = hashParams(hash);
  return normalizeCanvasViewportTarget({
    x: params.get(CANVAS_VIEWPORT_HASH_KEYS.x),
    y: params.get(CANVAS_VIEWPORT_HASH_KEYS.y),
    zoom: params.get(CANVAS_VIEWPORT_HASH_KEYS.zoom),
    duration: params.get(CANVAS_VIEWPORT_HASH_KEYS.duration),
  });
}

/** The node form of a canvas link: `#node=<id>` or `#nodes=<id>,<id>` fits
 *  the viewport to those nodes on arrival, as a block's
 *  `cohesive.navigate({ node })` does in place. */
export const CANVAS_NODE_HASH_KEYS = { node: "node", nodes: "nodes" } as const;

/** The node ids a hash names, in order and deduped; null when it names none. */
export function parseCanvasNodeHash(hash: string): string[] | null {
  const params = hashParams(hash);
  const raw = [
    params.get(CANVAS_NODE_HASH_KEYS.node) ?? "",
    params.get(CANVAS_NODE_HASH_KEYS.nodes) ?? "",
  ].join(",");
  const ids = Array.from(
    new Set(
      raw
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean),
    ),
  );
  return ids.length > 0 ? ids : null;
}

/** `hash` without the viewport and node keys; anything else in it survives,
 *  and an emptied hash is dropped outright rather than left as a bare `#`. */
export function stripCanvasViewportHash(hash: string): string {
  const params = hashParams(hash);
  for (const key of [
    ...Object.values(CANVAS_VIEWPORT_HASH_KEYS),
    ...Object.values(CANVAS_NODE_HASH_KEYS),
  ]) {
    params.delete(key);
  }
  const next = params.toString();
  return next ? `#${next}` : "";
}
